import { create } from 'zustand';
import type { Block } from '@hump/shared-types';
import { getStorage } from '../lib/storage/factory';
import { chatCompletion } from '../lib/ai/provider';
import { buildTranslatePrompt, parseTranslateResponse } from '../lib/ai/prompt';

interface TranslateState {
  translating: boolean;
  translatedCount: number;
  totalCount: number;
  error: string | null;
  cancelled: boolean;

  translateBlock: (docId: string, blockId: string) => Promise<void>;
  translateAll: (docId: string, blocks: Block[]) => Promise<void>;
  cancelTranslate: () => void;
}

function getUntranslatedBlocks(blocks: Block[]): Block[] {
  return blocks.filter((b) => b.transStatus !== 'done' && b.type !== 'code' && b.original.trim());
}

export const useTranslateStore = create<TranslateState>((set, get) => ({
  translating: false,
  translatedCount: 0,
  totalCount: 0,
  error: null,
  cancelled: false,

  translateBlock: async (docId, blockId) => {
    set({ translating: true, error: null });
    try {
      const storage = getStorage();
      const block = await storage.readBlock(docId, blockId);

      block.transStatus = 'pending';
      await storage.writeBlock(docId, block);

      const { system, user } = buildTranslatePrompt(
        [{ blockId: block.id, text: block.original }],
        'en', 'zh'
      );

      const result = await chatCompletion([
        { role: 'system', content: system },
        { role: 'user', content: user },
      ]);

      const translations = parseTranslateResponse(result, 1);
      block.translation = translations[0].trim();
      block.transStatus = 'done';
      block.updatedAt = new Date().toISOString();
      await storage.writeBlock(docId, block);

      set({ translating: false });
    } catch (e) {
      set({ translating: false, error: (e as Error).message });
      try {
        const storage = getStorage();
        const block = await storage.readBlock(docId, blockId);
        if (block.transStatus === 'pending') {
          block.transStatus = 'none';
          await storage.writeBlock(docId, block);
        }
      } catch { /* 静默 */ }
    }
  },

  translateAll: async (docId, blocks) => {
    const untranslated = getUntranslatedBlocks(blocks);
    if (untranslated.length === 0) return;

    set({ translating: true, translatedCount: 0, totalCount: untranslated.length, error: null });

    const storage = getStorage();
    const batchSize = 5;

    try {
      for (let i = 0; i < untranslated.length; i += batchSize) {
        // 4.9 检查取消标志
        if (get().cancelled) {
          // 回退 pending 为 none
          for (let j = i; j < untranslated.length; j++) {
            if (untranslated[j].transStatus === 'pending') {
              untranslated[j].transStatus = 'none';
              await storage.writeBlock(docId, untranslated[j]);
            }
          }
          set({ translating: false, cancelled: false });
          return;
        }

        const batch = untranslated.slice(i, i + batchSize);

        for (const b of batch) {
          b.transStatus = 'pending';
          await storage.writeBlock(docId, b);
        }

        const { system, user } = buildTranslatePrompt(
          batch.map((b) => ({ blockId: b.id, text: b.original })),
          'en', 'zh'
        );

        const result = await chatCompletion([
          { role: 'system', content: system },
          { role: 'user', content: user },
        ]);

        const translations = parseTranslateResponse(result, batch.length);

        for (let j = 0; j < batch.length; j++) {
          batch[j].translation = translations[j].trim();
          batch[j].transStatus = 'done';
          batch[j].updatedAt = new Date().toISOString();
          await storage.writeBlock(docId, batch[j]);
        }

        set({ translatedCount: Math.min(i + batchSize, untranslated.length) });
      }

      set({ translating: false });
    } catch (e) {
      set({ translating: false, error: (e as Error).message });
    }
  },

  cancelTranslate: () => set({ cancelled: true }),
}));
